import { MaskedLines, Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import { Link } from "react-router-dom";

const ARTICLES = [
  {
    numeral: "I",
    title: "Name and Purpose",
    body: [
      'Crusaders of the Cross (hereinafter referred to as "the Club"). The Club is a faith-based, Christian brotherhood dedicated to the lawful exercise of Second Amendment rights, rigorous firearms training, and steadfast spiritual fellowship. Our mission is to forge disciplined, capable individuals who protect their families and communities while reflecting the teachings of Jesus Christ.',
    ],
  },
  {
    numeral: "II",
    title: "Membership Eligibility",
    intro: "Membership is a privilege reserved for those who uphold the highest moral and ethical code. To be considered for membership, an applicant must strictly meet the following criteria:",
    items: [
      "Profess Jesus Christ as their Lord and Savior and align with foundational Christian doctrines.",
      "Be legally eligible to purchase and possess a firearm under all state and federal laws.",
      "Have zero convictions for any sex crimes or offenses involving minors.",
      "Have zero convictions for domestic violence (misdemeanor or felony).",
      'Submit to a background check and a probationary "Prospect" period before full admission.',
    ],
  },
  {
    numeral: "III",
    title: "Chain of Command and Ranks",
    intro: "The Club operates on a strict chain of command, and organizational structure to ensure safety, order, and accountability.",
    ranks: [
      ["President (Commander)", "The ultimate authority in the Club. Oversees all operations, commands meetings, and acts as the public face of the organization."],
      ["Vice President (Executive Officer)", "Second in command. Assumes command in the President's absence and coordinates the duties of the other officers."],
      ["Sergeant at Arms (Range Master)", "Responsible for physical security, strict enforcement of all firearms safety protocols on the range, and maintaining order during all Club gatherings. Defends the bylaws and handles disciplinary enforcement."],
      ["Chaplain", "The spiritual leader of the Club. Leads prayer, provides biblical counsel to members, and ensures the Club remains anchored in its Christian mission."],
      ["Secretary/Treasurer (Quartermaster)", "Manages all communications, member records, dues, and Club logistics."],
      ["Full Member (Patched)", "An individual who has successfully completed the Prospect phase, been voted in by the officers, and earned the right to wear the Club's insignia."],
      ["Prospect (Recruit)", "A probationary applicant undergoing mentorship, safety evaluations, and character assessment. Prospects have no voting rights."],
    ],
  },
  {
    numeral: "IV",
    title: "Code of Conduct and Range Protocol",
    codes: [
      ["Safety Absolute", "All members must always adhere strictly to the four cardinal rules of firearm safety. The Sergeant at Arms holds unquestionable authority on the firing line; failure to follow range commands results in immediate removal."],
      ["Christian Witness", "Members wear the name of Christ and the Club. Drunkenness, illicit drug use, unprovoked violence, or behavior bringing disrepute to the Christian faith or the Club will result in immediate disciplinary review."],
      ["Brotherhood", "Members are expected to defend, support, and pray for one another, operating as a cohesive unit in both spiritual warfare and physical training."],
    ],
  },
  {
    numeral: "V",
    title: "Discipline and Expulsion",
    body: [
      "Any member found violating the membership eligibility requirements post-admission (e.g., committing a felony or domestic violence offense) will be immediately stripped of their rank and expelled. Minor infractions regarding Club protocol or safety will be handled by the Sergeant at Arms and may result in suspension, remedial training, or expulsion by a majority vote of the leadership.",
    ],
  },
];

export default function Bylaws() {
  return (
    <main className="pt-16" data-testid="bylaws-page">
      <section className="relative overflow-hidden noise-overlay crosshair-grid border-b border-gold/15">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <p className="eyebrow mb-5">The Governing Word</p>
          <MaskedLines
            lines={["BYLAWS OF", "THE ORDER."]}
            lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-parchment"
          />
          <p className="mt-6 max-w-xl text-stone-300 text-sm sm:text-base leading-relaxed">
            Five articles govern Crusaders of the Cross — our purpose, our standards, our chain of command,
            our conduct, and our discipline. Every member swears to them.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 space-y-6" data-testid="bylaws-articles">
        {ARTICLES.map((a, i) => (
          <Reveal key={a.numeral} delay={0.05}>
            <article className="relative border border-gold/20 bg-card2/50 noise-overlay p-8 sm:p-12 overflow-hidden" data-testid={`bylaw-article-${a.numeral}`}>
              <span className="absolute -top-6 -right-4 font-display text-[10rem] leading-none text-outline select-none pointer-events-none opacity-60" aria-hidden="true">{a.numeral}</span>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">Article {a.numeral}</p>
              <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment mt-3">{a.title}</h2>

              {a.body && a.body.map((p, j) => (
                <p key={j} className="mt-6 text-stone-300 leading-relaxed text-sm sm:text-base">{p}</p>
              ))}
              {a.intro && <p className="mt-6 text-stone-300 leading-relaxed text-sm sm:text-base">{a.intro}</p>}

              {a.items && (
                <ul className="mt-6 space-y-3">
                  {a.items.map((item, j) => (
                    <li key={j} className="flex gap-4 items-start">
                      <span className="font-display text-gold leading-none mt-1">{String(j + 1).padStart(2, "0")}</span>
                      <p className="text-stone-300 leading-relaxed text-sm sm:text-base">{item}</p>
                    </li>
                  ))}
                </ul>
              )}

              {a.ranks && (
                <div className="mt-8 space-y-px">
                  {a.ranks.map(([rank, desc], j) => (
                    <div key={j} className="border border-gold/15 bg-obsidian/50 px-6 py-5 hover:border-gold/40 transition-colors duration-300" data-testid={`rank-${j}`}>
                      <div className="flex items-baseline gap-4 flex-wrap">
                        <span className="font-mono text-[10px] text-crimson tracking-[0.25em]">{String(j + 1).padStart(2, "0")}</span>
                        <h3 className="font-condensed uppercase tracking-wider text-lg text-gold">{rank}</h3>
                      </div>
                      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {a.codes && (
                <div className="mt-8 space-y-px">
                  {a.codes.map(([name, desc], j) => (
                    <div key={j} className="border border-gold/15 bg-obsidian/50 px-6 py-5 hover:border-crimson/40 transition-colors duration-300" data-testid={`code-${j}`}>
                      <h3 className="font-condensed uppercase tracking-wider text-lg text-parchment">{name}</h3>
                      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </Reveal>
        ))}

        <Reveal className="text-center pt-10">
          <LogoMark className="w-14 h-14 mx-auto mb-6 drop-shadow-[0_0_22px_rgba(212,175,55,0.35)]" />
          <p className="font-serif italic text-stone-400">Sworn before God and witnessed by the brotherhood.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/handbook.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="handbook-download-button"
              className="border border-crimson/50 text-crimson font-condensed uppercase tracking-[0.22em] px-8 py-4 hover:bg-crimson hover:text-parchment hover:shadow-[0_0_24px_rgba(178,34,34,0.35)] transition-[background-color,color,box-shadow] duration-300"
            >
              Member Handbook (PDF)
            </a>
            <Link to="/join" data-testid="bylaws-join-link" className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold px-10 py-4 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-[background-color,box-shadow] duration-300">
              Apply Under These Bylaws
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
